/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getTokenData, isResponseSuccess, formatFilters } from 'src/utils/api';
import { LOADING_STATUSES } from 'src/constants/statuses';
import { formatDates, handleApiError } from 'src/utils/views/users';
import { showNotification } from 'src/utils/notifications';
import { INITIAL_FILTERS_STATE } from '../constants/users';
import API from '../../../api/api';

// Action Types
export const USERS_ACTIONS = {
  LOAD: 'users/load',
  LOAD_BY_ID: 'users/loadById',
  LOAD_BY_EMAIL: 'users/loadByEmail',
  DELETE: 'users/deleteById',
  BECOME_USER: 'users/becomeUser',
  UPDATE_USER: 'users/updateUser',
  FILTER_APPLY: 'users/filterApply',
  CHANGE_PAGE: 'users/usersChangePageAction',
  EXPORT: 'users/export',
  GET_NETWORKS: 'users/get_networks',
  UPDATE_NETWORKS: 'users/update_networks',
  GET_SOURCES: 'users/get_sources',
  UPDATE_SOURCES: 'users/update_sources',
  CAMPAIGN_CHANGE_TYPE: 'users/campaign_change_type',
};

export const loadUsers = createAsyncThunk(
  USERS_ACTIONS.LOAD,
  async (_, { getState, rejectWithValue }) => {
    try {
      const { filters, page, per } = getState().usersReducer;
      const formattedFilters = formatDates(formatFilters(filters));

      const response = await API(getTokenData()).USERS.load({
        ...formattedFilters,
        page: page + 1,
        per,
      });

      return isResponseSuccess(response.status)
        ? { data: response.data, totalItems: response.total }
        : {};
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const loadUsersById = createAsyncThunk(
  USERS_ACTIONS.LOAD_BY_ID,
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await API(getTokenData()).USERS.loadById(id);
      return isResponseSuccess(response.status) ? response.data : null;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);


export const loadUsersByEmail = createAsyncThunk(
  USERS_ACTIONS.LOAD_BY_EMAIL,
  async ({ email }, { rejectWithValue }) => {
    try {
      const response = await API(getTokenData()).USERS.load({
        email,
      });
      return isResponseSuccess(response.status) ? response.data : [];
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const deleteUsersById = createAsyncThunk(
  USERS_ACTIONS.DELETE,
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await API(getTokenData()).USERS.delete(id);
      return isResponseSuccess(response.status) ? response.data : {};
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const usersBecomeLogin = createAsyncThunk(
  USERS_ACTIONS.BECOME_USER,
  async ({ id }, { getState, rejectWithValue }) => {
    try {
      const { APP_HOST } = getState().userReducer.env;

      if (!APP_HOST) {
        showNotification({
          type: 'error',
          text: 'Cannot open the client panel: APP_HOST is not loaded. Reload the page and try again.',
          timeout: 5000,
        });
        return rejectWithValue('APP_HOST is not loaded');
      }

      const response = await API(getTokenData()).USERS.become(id);

      if (isResponseSuccess(response.status)) {
        // The fragment never reaches a server or a Referer; the client panel trades the
        // one-time code for the session's tokens and drops it from history.
        const url = `${APP_HOST.replace(/\/+$/, '')}/become#code=${response.data.code}`;
        window.open(url, '_blank');
        return response.data;
      }
      return {};
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const usersUpdateSelectedUser = createAsyncThunk(
  USERS_ACTIONS.UPDATE_USER,
  async ({ data, enqueueSnackbar, goBack }, { rejectWithValue }) => {
    try {
      const newData = {
        ...data,
        clicks_left: Number(data.clicks_left),
        credits: {
          ...data.credits,
          revenue: Number(data.credits.revenue),
        },
      };
      const response = await API(getTokenData()).USERS.update(newData);

      if (isResponseSuccess(response.status)) {
        enqueueSnackbar('User updated successfully!', {
          variant: 'success',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'right',
          },
          autoHideDuration: 3000,
        });
        if (goBack) {
          goBack();
        }
        return response.data;
      }
      const message = response.data?.error || 'Failed to update user!';
      const isNetworkFailure = !response.status;
      if (isNetworkFailure) {
        enqueueSnackbar(message, {
          variant: 'error',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'right',
          },
          autoHideDuration: 5000,
        });
      }
      return rejectWithValue(message);
    } catch (error) {
      return rejectWithValue(handleApiError(error, enqueueSnackbar));
    }
  },
);

export const getUsersNetworks = createAsyncThunk(
  USERS_ACTIONS.GET_NETWORKS,
  async (data, { rejectWithValue }) => {
    try {
      const response = await API(getTokenData()).USERS.getNetworks(data.id, {
        ...data,
      });
      return isResponseSuccess(response.status) ? response.data : {};
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const updateUsersNetworks = createAsyncThunk(
  USERS_ACTIONS.UPDATE_NETWORKS,
  async (data, { rejectWithValue }) => {
    try {
      const response = await API(getTokenData()).USERS.updateNetworks(data);
      showNotification({
        type: 'success',
        text: 'Currency for network updated successfully!',
        timeout: 5000,
      });
      return isResponseSuccess(response.status) ? response.data : {};
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const getUsersSource = createAsyncThunk(
  USERS_ACTIONS.GET_SOURCES,
  async (data, { rejectWithValue }) => {
    try {
      const response = await API(getTokenData()).USERS.getSources(data.id, {
        ...data
      });
      return isResponseSuccess(response.status) ? response.data : {};
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const updateUsersSources = createAsyncThunk(
  USERS_ACTIONS.UPDATE_SOURCES,
  async (data, { rejectWithValue }) => {
    try {
      const response = await API(getTokenData()).USERS.updateSources(data);
      showNotification({
        type: 'success',
        text: 'Currency for source updated successfully!',
        timeout: 5000,
      });
      return isResponseSuccess(response.status) ? response.data : {};
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const updateCampaignType = createAsyncThunk(
  USERS_ACTIONS.CAMPAIGN_CHANGE_TYPE,
  async (data, { rejectWithValue }) => {
    try {
      const response = await API(getTokenData()).USERS.updateParallelTracking({
        ...data,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  },
);

const initialState = {
  entity: [],
  page: 0,
  per: 100,
  totalItems: 0,
  filters: INITIAL_FILTERS_STATE,
  status: LOADING_STATUSES.IDLE,
  selectedUser: {},
  selectedUserStatus: LOADING_STATUSES.IDLE,
  error: null,
};

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    setSelectedUser: (state, { payload }) => {
      state.selectedUser = payload;
    },
    setFilters: (state, { payload }) => {
      state.filters = { ...state.filters, ...payload };
    },
    setPageData: (state, { payload: { page, per } }) => {
      state.page = page;
      state.per = per;
    },
    resetUsers: (state) => ({
      ...state,
      ...{
        entity: [],
        page: 0,
        per: 100,
        totalItems: 0,
        filters: INITIAL_FILTERS_STATE,
        status: LOADING_STATUSES.IDLE,
      },
    }),
    resetSelectedUser: (state) => ({
      ...state,
      ...{
        selectedUser: {},
        selectedUserStatus: LOADING_STATUSES.IDLE,
      },
    }),
  },
  extraReducers: (builder) => {
    const addLoadingCase = (builder, action, statusField = 'status') => {
      builder.addCase(action.pending, (state) => {
        state[statusField] = LOADING_STATUSES.LOADING;
      });
    };

    const addSuccessCase = (builder, action, dataField = 'entity', statusField = 'status') => {
      builder.addCase(action.fulfilled, (state, { payload }) => {
        if (dataField === 'entity') {
          state.entity = payload?.data || [];
          state.totalItems = payload?.totalItems || 0;
        } else {
          state[dataField] = payload || {};
        }
        state[statusField] = LOADING_STATUSES.SUCCEEDED;
      });
    };

    const addErrorCase = (builder, action, statusField = 'status') => {
      builder.addCase(action.rejected, (state, { payload }) => {
        state[statusField] = LOADING_STATUSES.FAILED;
        state.error = payload;
      });
    };

    // Networks
    addLoadingCase(builder, getUsersNetworks);
    addSuccessCase(builder, getUsersNetworks);
    addErrorCase(builder, getUsersNetworks);
    addLoadingCase(builder, updateUsersNetworks);
    addSuccessCase(builder, updateUsersNetworks);
    addErrorCase(builder, updateUsersNetworks);

    // Sources
    addLoadingCase(builder, getUsersSource);
    addSuccessCase(builder, getUsersSource);
    addErrorCase(builder, getUsersSource);
    addLoadingCase(builder, updateUsersSources);
    addSuccessCase(builder, updateUsersSources);
    addErrorCase(builder, updateUsersSources);

    addLoadingCase(builder, updateCampaignType);
    addSuccessCase(builder, updateCampaignType);
    addErrorCase(builder, updateCampaignType);

    // Load Users
    addLoadingCase(builder, loadUsers);
    addSuccessCase(builder, loadUsers);
    addErrorCase(builder, loadUsers);

    // Load Users By Id
    addLoadingCase(builder, loadUsersById, 'selectedUserStatus');
    addSuccessCase(builder, loadUsersById, 'selectedUser', 'selectedUserStatus');
    addErrorCase(builder, loadUsersById, 'selectedUserStatus');

    // Delete User By Id
    addLoadingCase(builder, deleteUsersById);
    addSuccessCase(builder, deleteUsersById);
    addErrorCase(builder, deleteUsersById);

    // Update Selected User
    addLoadingCase(builder, usersUpdateSelectedUser, 'selectedUserStatus');
    addSuccessCase(builder, usersUpdateSelectedUser, 'selectedUser', 'selectedUserStatus');
    addErrorCase(builder, usersUpdateSelectedUser, 'selectedUserStatus');
  },
});

export const {
  setSelectedUser,
  setFilters,
  setPageData,
  resetUsers,
  resetSelectedUser,
} = usersSlice.actions;

export default usersSlice.reducer;

export const usersFilterApply = createAsyncThunk(
  USERS_ACTIONS.FILTER_APPLY,
  async (newFilters, { dispatch }) => {
    await Promise.all([
      dispatch(setFilters(newFilters)),
      dispatch(loadUsers()),
    ]);
  },
);

export const usersChangePageAction = createAsyncThunk(
  USERS_ACTIONS.CHANGE_PAGE,
  async ({ page = 0, per = 0 }, { dispatch, rejectWithValue }) => {
    try {
      await dispatch(setPageData({ page, per }));
      await dispatch(loadUsers());
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);

export const exportUsers = createAsyncThunk(
  USERS_ACTIONS.EXPORT,
  async (_, { getState, rejectWithValue }) => {
    try {
      const { filters } = getState().usersReducer;
      const formattedFilters = formatDates(formatFilters(filters));

      const exportParams = [
        'id',
        'email',
        'messenger',
        'plan',
        'sort',
        'date_from',
        'date_to',
        'domain',
        'promo_code',
      ].reduce((result, key) => {
        const value = formattedFilters[key];

        if (value) {
          result[key] = value;
        }

        return result;
      }, {});

      if (formattedFilters.status != null && formattedFilters.status !== '') {
        exportParams.status = formattedFilters.status;
      }

      const response = await API(getTokenData()).USERS.export(exportParams);

      if (isResponseSuccess(response.status)) {
        showNotification({
          type: 'success',
          text: 'Export started! CSV file will be sent to your email.',
          timeout: 5000,
        });
        return response.data;
      }
      return {};
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  },
);
